import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError, useAuth } from "@/contexts/AuthContext";
import {
  ShoppingCart, FileText, Plus, Minus, Trash2, Download, Package, ArrowLeft,
  ExternalLink, ClipboardList, Loader2,
} from "lucide-react";

const CATEGORY_LABELS = {
  brochure: "Brochure",
  rate_card: "Rate Card",
  flyer: "Flyer",
  poster: "Poster",
  table_topper: "Table Topper",
  desktop_laminate: "Desktop Laminate",
  banner: "Banner",
  business_card: "Business Card",
  other: "Other",
};
const PROGRAM_LABELS = {
  general: "General",
  essentials: "Pre-Owned VSC",
  franchise: "Franchise VSC",
  ancillary: "4n1 Ancillary",
  wear_guard: "Wear Guard",
  gap: "GAP",
  reinsurance: "Reinsurance",
};

const VERTICAL_LABELS = {
  automotive: "Automotive",
  rv: "RV",
  powersports: "Powersports",
  marine: "Marine",
};

export default function MarketingCatalog() {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVertical, setFilterVertical] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterProgram, setFilterProgram] = useState("all");
  const [cart, setCart] = useState(() => {
    try {
      const s = localStorage.getItem("awg_marketing_cart");
      return s ? JSON.parse(s) : {};
    } catch (e) { return {}; }
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("awg_marketing_cart", JSON.stringify(cart));
  }, [cart]);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/materials");
      setMaterials(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMaterials(); }, []);

  const filtered = useMemo(() => {
    return materials.filter((m) =>
      (filterVertical === "all" || m.vertical_slug === filterVertical) &&
      (filterCategory === "all" || m.category === filterCategory) &&
      (filterProgram === "all" || (m.program || "general") === filterProgram)
    );
  }, [materials, filterVertical, filterCategory, filterProgram]);

  const cartItems = useMemo(() => {
    return Object.entries(cart).map(([id, qty]) => {
      const m = materials.find((x) => x.id === id);
      return m ? { ...m, qty } : null;
    }).filter(Boolean);
  }, [cart, materials]);

  const cartCount = useMemo(() => cartItems.reduce((s, i) => s + i.qty, 0), [cartItems]);

  const addToCart = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const setQty = (id, qty) => setCart((c) => {
    const next = { ...c };
    if (qty <= 0) delete next[id];
    else next[id] = qty;
    return next;
  });
  const removeFromCart = (id) => setQty(id, 0);
  const clearCart = () => setCart({});

  const downloadFile = async (m) => {
    try {
      const { data, headers } = await api.get(`/marketing/materials/${m.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = m.filename || `${m.title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed — file may not be uploaded yet.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="marketing-catalog-page">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-2">
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Package size={12} /> Marketing materials
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1">
              Order marketing collateral
            </h1>
            <p className="text-sm text-[#6C757D] mt-2">Download PDFs on demand or add hard copies to your cart for fulfillment.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/marketing/my-orders">
              <Button variant="outline" size="sm" className="h-9 rounded-sm border-[#0B132B]/20" data-testid="marketing-my-orders-btn">
                <ClipboardList size={12} className="mr-1.5" /> My orders
              </Button>
            </Link>
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-9 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90" data-testid="marketing-cart-btn" disabled={cartCount === 0}>
                  <ShoppingCart size={12} className="mr-1.5" /> Cart ({cartCount})
                </Button>
              </DialogTrigger>
              <CheckoutDialog
                cartItems={cartItems}
                setQty={setQty}
                removeFromCart={removeFromCart}
                clearCart={clearCart}
                user={user}
                onSuccess={() => { setCheckoutOpen(false); toast.success("Order submitted!"); }}
              />
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-5 border-[#E9ECEF] rounded-sm bg-white flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#6C757D]">Program</span>
            <Select value={filterProgram} onValueChange={setFilterProgram}>
              <SelectTrigger className="h-9 w-[200px] rounded-sm" data-testid="filter-program">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All programs</SelectItem>
                {Object.entries(PROGRAM_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#6C757D]">Vertical</span>
            <Select value={filterVertical} onValueChange={setFilterVertical}>
              <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="filter-vertical">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All verticals</SelectItem>
                {Object.entries(VERTICAL_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#6C757D]">Category</span>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading materials…</div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-[#E9ECEF]" data-testid="materials-empty">
            <Package size={32} className="mx-auto text-[#6C757D] mb-2" />
            <div className="font-bold text-[#0B132B]">No materials available yet</div>
            <div className="text-sm text-[#6C757D] mt-1">Admin will publish brochures and rate cards here soon.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="materials-grid">
            {filtered.map((m) => (
              <Card key={m.id} className="p-4 border-[#E9ECEF] rounded-sm bg-white hover:shadow-md transition-shadow flex flex-col" data-testid={`material-card-${m.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37]">
                    {CATEGORY_LABELS[m.category] || m.category}
                    {m.vertical_slug && ` · ${VERTICAL_LABELS[m.vertical_slug] || m.vertical_slug}`}
                  </div>
                  <FileText size={14} className="text-[#6C757D] shrink-0" />
                </div>
                <div className="font-bold text-[#0B132B] leading-snug mb-1">{m.title}</div>
                {(m.program && m.program !== "general") && (
                  <div className="text-[10px] mb-2">
                    <span className="px-1.5 py-0.5 rounded-sm border border-[#0B132B]/15 bg-[#F8F9FA] text-[#0B132B] font-bold uppercase tracking-wider" data-testid={`material-program-${m.id}`}>
                      {PROGRAM_LABELS[m.program] || m.program}
                    </span>
                  </div>
                )}
                {m.description && <div className="text-xs text-[#6C757D] mb-3 line-clamp-3">{m.description}</div>}
                <div className="flex-1"></div>
                <div className="flex items-center gap-2 pt-3 border-t border-[#F1F3F5] mt-3">
                  {m.has_file && (
                    <Button variant="outline" size="sm" onClick={() => downloadFile(m)}
                      className="h-8 rounded-sm border-[#0B132B]/20 flex-1"
                      data-testid={`material-download-${m.id}`}>
                      <Download size={12} className="mr-1.5" /> PDF
                    </Button>
                  )}
                  <Button size="sm" onClick={() => addToCart(m.id)}
                    className="h-8 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90 flex-1"
                    data-testid={`material-add-${m.id}`}>
                    <Plus size={12} className="mr-1.5" /> Order hard copy
                  </Button>
                </div>
                {cart[m.id] && (
                  <div className="mt-2 text-xs text-[#D4AF37] font-bold" data-testid={`material-incart-${m.id}`}>
                    In cart: {cart[m.id]}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CheckoutDialog({ cartItems, setQty, removeFromCart, clearCart, user, onSuccess }) {
  const [shipTo, setShipTo] = useState(() => {
    try {
      const s = localStorage.getItem("awg_marketing_shipto");
      return s ? JSON.parse(s) : {
        name: user?.name || "", company: user?.agency || "",
        address1: "", address2: "", city: "", state: "", zip: "", phone: "",
      };
    } catch (e) {
      return { name: user?.name || "", company: user?.agency || "", address1: "", address2: "", city: "", state: "", zip: "", phone: "" };
    }
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = cartItems.length > 0 && shipTo.name && shipTo.address1 && shipTo.city && shipTo.state && shipTo.zip;

  const submit = async () => {
    if (!canSubmit) { toast.error("Fill in required shipping fields"); return; }
    setSubmitting(true);
    try {
      const items = cartItems.map((i) => ({ material_id: i.id, qty: i.qty }));
      await api.post("/marketing/orders", { items, ship_to: shipTo, notes: notes || null });
      localStorage.setItem("awg_marketing_shipto", JSON.stringify(shipTo));
      clearCart();
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
      <DialogHeader>
        <DialogTitle>Review order & ship-to</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {/* Cart items */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider font-bold text-[#6C757D]">Cart</div>
          {cartItems.length === 0 && <div className="text-sm text-[#6C757D] italic">Cart is empty.</div>}
          {cartItems.map((i) => (
            <div key={i.id} className="flex items-center gap-2 p-2 border border-[#E9ECEF] rounded-sm bg-[#F8F9FA]">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0B132B] truncate">{i.title}</div>
                <div className="text-[11px] text-[#6C757D]">{CATEGORY_LABELS[i.category] || i.category}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7 rounded-sm" onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`}>
                  <Minus size={12} />
                </Button>
                <Input value={i.qty} onChange={(e) => setQty(i.id, Math.max(0, parseInt(e.target.value) || 0))}
                  className="h-7 w-14 text-center rounded-sm text-sm" data-testid={`cart-qty-${i.id}`} />
                <Button variant="outline" size="icon" className="h-7 w-7 rounded-sm" onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`}>
                  <Plus size={12} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => removeFromCart(i.id)} data-testid={`cart-remove-${i.id}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Ship-to */}
        <div className="space-y-3 pt-2 border-t border-[#E9ECEF]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#6C757D]">Ship to</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Recipient name *</Label>
              <Input value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} className="rounded-sm h-9" data-testid="ship-name" />
            </div>
            <div>
              <Label className="text-xs">Company</Label>
              <Input value={shipTo.company} onChange={(e) => setShipTo({ ...shipTo, company: e.target.value })} className="rounded-sm h-9" data-testid="ship-company" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Address line 1 *</Label>
              <Input value={shipTo.address1} onChange={(e) => setShipTo({ ...shipTo, address1: e.target.value })} className="rounded-sm h-9" data-testid="ship-address1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Address line 2</Label>
              <Input value={shipTo.address2} onChange={(e) => setShipTo({ ...shipTo, address2: e.target.value })} className="rounded-sm h-9" data-testid="ship-address2" />
            </div>
            <div>
              <Label className="text-xs">City *</Label>
              <Input value={shipTo.city} onChange={(e) => setShipTo({ ...shipTo, city: e.target.value })} className="rounded-sm h-9" data-testid="ship-city" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">State *</Label>
                <Input value={shipTo.state} onChange={(e) => setShipTo({ ...shipTo, state: e.target.value })} className="rounded-sm h-9" data-testid="ship-state" />
              </div>
              <div>
                <Label className="text-xs">ZIP *</Label>
                <Input value={shipTo.zip} onChange={(e) => setShipTo({ ...shipTo, zip: e.target.value })} className="rounded-sm h-9" data-testid="ship-zip" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={shipTo.phone} onChange={(e) => setShipTo({ ...shipTo, phone: e.target.value })} className="rounded-sm h-9" data-testid="ship-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes for fulfillment</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm text-sm" data-testid="ship-notes" />
          </div>
        </div>

        <div className="pt-3 border-t border-[#E9ECEF] text-xs text-[#6C757D]">
          Contact tied to this order: <b className="text-[#0B132B]">{user?.name} · {user?.email}</b>
          {user?.agency && <> · {user.agency}</>}
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button variant="outline" size="sm" onClick={clearCart} disabled={cartItems.length === 0} data-testid="cart-clear-btn">Clear cart</Button>
        <Button size="sm" onClick={submit} disabled={!canSubmit || submitting}
          className="bg-[#0B132B] text-white hover:bg-[#0B132B]/90" data-testid="order-submit-btn">
          {submitting ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Submitting…</> : "Submit order"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
